import { useState, useEffect, useCallback } from 'react';
import {
  Shield, TrendingUp, TrendingDown, Minus, Save, RefreshCw, Loader2,
  AlertTriangle, CheckCircle2, Info, Users, Award, MapPin, Activity, BarChart3,
  ChevronDown, ChevronUp, Clock
} from 'lucide-react';
import { Modal } from '../ui/Modal';
import {
  creditScoreApi,
  type CreditScoreStats,
  type CreditScoreHistoryItem,
  type TerritorialContext,
} from '../../services/api/endpoints/creditScore';
import { getScoreColor, getScoreBg, getScoreLabel, getRiskLevelLabel } from '../../utils/creditScoreHelpers';



// ─── CET field configuration ──────────────────────────────────────────────

const CET_FIELDS: {
  key: keyof TerritorialContext;
  label: string;
  description: string;
  icon: React.ReactNode;
  type: 'slider' | 'toggle';
}[] = [
  {
    key: 'score_reseau_communautaire',
    label: 'Réseau communautaire',
    description: 'Force du réseau social et business local',
    icon: <Users className="h-4 w-4" />,
    type: 'slider',
  },
  {
    key: 'appartenance_association',
    label: 'Association professionnelle',
    description: "Membre d'une association professionnelle reconnue",
    icon: <Award className="h-4 w-4" />,
    type: 'toggle',
  },
  {
    key: 'score_reputation_terrain',
    label: 'Réputation terrain',
    description: 'Réputation locale évaluée sur le terrain',
    icon: <Shield className="h-4 w-4" />,
    type: 'slider',
  },
  {
    key: 'diversification_activites',
    label: 'Diversification',
    description: 'Diversification des sources de revenus',
    icon: <BarChart3 className="h-4 w-4" />,
    type: 'slider',
  },
  {
    key: 'exposition_zones_instables',
    label: 'Exposition zones instables',
    description: '0 = stable, 1 = très instable',
    icon: <MapPin className="h-4 w-4" />,
    type: 'slider',
  },
];

// ─── Component ────────────────────────────────────────────────────────────

interface CreditScoreCETModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  companyName: string;
  /** Called after a successful CET save or recalculation so parent can refresh stats */
  onScoreUpdated?: (companyId: string) => void;
}

export function CreditScoreCETModal({
  isOpen,
  onClose,
  companyId,
  companyName,
  onScoreUpdated,
}: CreditScoreCETModalProps) {
  // ─── State ────────────────────────────────────────────────────────────
  const [stats, setStats] = useState<CreditScoreStats | null>(null);
  const [history, setHistory] = useState<CreditScoreHistoryItem[]>([]);
  const [cetValues, setCetValues] = useState<Partial<TerritorialContext>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [cetDirty, setCetDirty] = useState(false);

  // ─── Data fetching ────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    const [statsRes, historyRes] = await Promise.all([
      creditScoreApi.getStats(companyId),
      creditScoreApi.getHistory(companyId, { limit: 10 }),
    ]);

    if (statsRes.success && statsRes.data) {
      setStats(statsRes.data.stats);
    }
    if (historyRes.success && historyRes.data) {
      setHistory(historyRes.data.history ?? []);
    }
    if (!statsRes.success && !historyRes.success) {
      // Both failed — likely no score data yet, not necessarily an error
      setStats(null);
      setHistory([]);
    }

    setLoading(false);
  }, [companyId]);

  useEffect(() => {
    if (isOpen && companyId) {
      loadData();
      // Reset form state when modal opens
      setCetValues({});
      setCetDirty(false);
      setSaveSuccess(false);
    }
  }, [isOpen, companyId, loadData]);

  // ─── CET field handlers ───────────────────────────────────────────────
  const handleCetChange = (key: keyof TerritorialContext, value: number) => {
    setCetValues((prev) => ({ ...prev, [key]: value }));
    setCetDirty(true);
    setSaveSuccess(false);
  };

  const handleSaveCet = async () => {
    // Validate values
    for (const [key, val] of Object.entries(cetValues)) {
      if (typeof val === 'number' && (val < 0 || val > 1)) {
        setError(`La valeur de ${key} doit être entre 0 et 1`);
        return;
      }
    }

    if (Object.keys(cetValues).length === 0) return;

    setSaving(true);
    setError(null);
    setSaveSuccess(false);

    const result = await creditScoreApi.updateTerritorialContext(companyId, cetValues);
    setSaving(false);

    if (result.success) {
      setSaveSuccess(true);
      setCetDirty(false);
      // Refresh stats in case score was affected
      const statsRes = await creditScoreApi.getStats(companyId);
      if (statsRes.success && statsRes.data) setStats(statsRes.data.stats);
    } else {
      setError(result.error || 'Erreur lors de la sauvegarde');
    }
  };

  const handleRecalculate = async () => {
    setCalculating(true);
    setError(null);

    const result = await creditScoreApi.calculate(companyId);
    setCalculating(false);

    if (result.success) {
      // Reload everything to show new score
      await loadData();
      onScoreUpdated?.(companyId);
    } else {
      setError(result.error || 'Erreur lors du recalcul');
    }
  };

  // ─── Render helpers ───────────────────────────────────────────────────
  const trendIcon = stats?.trend === 'up'
    ? <TrendingUp className="h-4 w-4 text-emerald-500" />
    : stats?.trend === 'down'
      ? <TrendingDown className="h-4 w-4 text-red-500" />
      : <Minus className="h-4 w-4 text-muted-foreground" />;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`CET — ${companyName}`} size="lg">
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Chargement des données...</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* ─── Score overview ─────────────────────────────────────────── */}
          <div className={`rounded-xl border p-4 ${stats ? getScoreBg(stats.currentScore) : 'bg-muted/30 border-border'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                  Cote de crédit actuelle
                </p>
                {stats ? (
                  <div className="flex items-baseline gap-3">
                    <span className={`text-4xl font-bold ${getScoreColor(stats.currentScore)}`}>
                      {stats.currentScore}
                    </span>
                    <span className="text-sm font-medium text-foreground/70">/ 100</span>
                    <span className={`text-sm font-semibold ${getScoreColor(stats.currentScore)}`}>
                      {getScoreLabel(stats.currentScore)}
                    </span>
                    {trendIcon}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-muted-foreground">—</span>
                    <span className="text-sm text-muted-foreground">Aucun score disponible</span>
                  </div>
                )}
              </div>
              {stats && (
                <div className="text-right text-xs text-muted-foreground space-y-0.5 hidden sm:block">
                  <p>Moy: {stats.averageScore.toFixed(0)} · Min: {stats.minScore} · Max: {stats.maxScore}</p>
                  <p>{stats.totalCalculations} calcul{stats.totalCalculations > 1 ? 's' : ''}</p>
                  {stats.lastCalculatedAt && (
                    <p>Dernier: {new Date(stats.lastCalculatedAt).toLocaleDateString('fr-FR')}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ─── CET Variables form ────────────────────────────────────── */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Activity className="h-4 w-4 text-primary" />
              Contexte Économique Territorial (CET)
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              Évaluez les 5 variables terrain de cette entreprise. Les champs modifiés seront sauvegardés.
            </p>

            <div className="space-y-4">
              {CET_FIELDS.map((field) => {
                const val = cetValues[field.key] as number | undefined;

                return (
                  <div key={field.key} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary mt-0.5">
                      {field.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-sm font-medium text-foreground">
                          {field.label}
                        </label>
                        {field.type === 'toggle' ? (
                          <button
                            type="button"
                            onClick={() => handleCetChange(field.key, val === 1 ? 0 : 1)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              val === 1 ? 'bg-primary' : 'bg-muted-foreground/30'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                val === 1 ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        ) : (
                          <span className="text-sm font-mono text-foreground/70 tabular-nums w-12 text-right">
                            {val !== undefined ? val.toFixed(2) : '—'}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mb-1.5">{field.description}</p>
                      {field.type === 'slider' && (
                        <input
                          type="range"
                          min={0}
                          max={1}
                          step={0.05}
                          value={val ?? 0.5}
                          onChange={(e) => handleCetChange(field.key, parseFloat(e.target.value))}
                          className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-muted-foreground/20
                            [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                            [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:shadow
                            [&::-webkit-slider-thumb]:cursor-pointer
                            [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full
                            [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Error / Success */}
            {error && (
              <div className="mt-3 flex items-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {error}
              </div>
            )}
            {saveSuccess && (
              <div className="mt-3 flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2 rounded-lg">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                Contexte territorial sauvegardé avec succès
              </div>
            )}

            {/* Action buttons */}
            <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <button
                onClick={handleSaveCet}
                disabled={saving || !cetDirty}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                  bg-primary text-primary-foreground hover:bg-primary/90
                  disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Sauvegarder le CET
              </button>
              <button
                onClick={handleRecalculate}
                disabled={calculating}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                  border border-border bg-background text-foreground hover:bg-muted
                  disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {calculating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Recalculer le score
              </button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              <Info className="inline h-3 w-3 mr-1" />
              La sauvegarde du CET ne déclenche pas automatiquement un recalcul. Cliquez sur « Recalculer » pour actualiser le score.
            </p>
          </div>

          {/* ─── Score history (collapsible) ───────────────────────────── */}
          <div className="border-t border-border pt-4">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors w-full text-left"
            >
              <Clock className="h-4 w-4 text-primary" />
              Historique des scores ({history.length})
              {showHistory ? <ChevronUp className="h-4 w-4 ml-auto" /> : <ChevronDown className="h-4 w-4 ml-auto" />}
            </button>

            {showHistory && (
              <div className="mt-3">
                {history.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    Aucun historique disponible.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {history.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/30 border border-border text-sm"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className={`font-bold tabular-nums ${getScoreColor(item.score)}`}>
                            {item.score}
                          </span>
                          {item.riskLevel && (
                            <span className="text-xs text-muted-foreground">
                              {getRiskLevelLabel(item.riskLevel)}
                            </span>
                          )}
                          {item.scoreChange && (
                            <span className={`text-xs font-medium ${
                              item.scoreChange.direction === 'up' ? 'text-emerald-600' :
                              item.scoreChange.direction === 'down' ? 'text-red-600' :
                              'text-muted-foreground'
                            }`}>
                              {item.scoreChange.direction === 'up' ? '+' : item.scoreChange.direction === 'down' ? '' : ''}
                              {item.scoreChange.change}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground text-right shrink-0 ml-2">
                          <div>{new Date(item.calculatedAt).toLocaleDateString('fr-FR')}</div>
                          <div className="text-muted-foreground/60 capitalize">
                            {item.trigger === 'scheduled' ? 'Auto' : item.trigger === 'api_request' ? 'Manuel' : item.trigger}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
